import {
  getAnalytics,
  isSupported,
  logEvent,
  setUserId,
  setUserProperties,
  Analytics,
} from "firebase/analytics";
import { app } from "auth/FirebaseAuth";
import { sanitizeGa4Params } from "utils/tracking";

/**
 * Track D — camada de product analytics (Firebase Analytics / GA4).
 *
 * Complementa o Datadog (ótimo para filtro/agregação de logs) com funis, retenção
 * e user-properties, que são nativos no GA4. Reusa a app Firebase já existente
 * (nenhum fornecedor novo). No-op seguro se o GA4 não estiver configurado
 * (measurementId ausente) ou não suportado no ambiente.
 *
 * Só envia fora de "development", espelhando o restante da telemetria.
 */

const isEnabled = process.env.NODE_ENV !== "development";

let analytics: Analytics | null = null;
let initPromise: Promise<Analytics | null> | null = null;

function ensureAnalytics(): Promise<Analytics | null> {
  if (analytics) return Promise.resolve(analytics);
  if (!isEnabled) return Promise.resolve(null);

  if (!initPromise) {
    initPromise = isSupported()
      .then((supported) => {
        if (!supported) return null;
        try {
          analytics = getAnalytics(app);
        } catch (err) {
          // Ex.: measurementId ausente. Segue sem GA4.
          console.error("[tracking] getAnalytics failed", err);
          analytics = null;
        }
        return analytics;
      })
      .catch(() => null);
  }

  return initPromise;
}

/** Define usuário pseudônimo e propriedades para segmentação (funis/retenção por cargo). */
export function faIdentify(userId: string | undefined, roleNames: string[] = []) {
  if (!userId) return;

  ensureAnalytics().then((a) => {
    if (!a) return;
    try {
      setUserId(a, userId);
      setUserProperties(a, {
        role: (roleNames.join(", ") || "unknown").slice(0, 36),
      });
    } catch (err) {
      console.error("[tracking] faIdentify failed", err);
    }
  });
}

/** Registra um evento no GA4. `subtype` vira `dashboard_<subtype>`. */
export function faTrack(subtype: string, params: Record<string, any>) {
  ensureAnalytics().then((a) => {
    if (!a) return;
    try {
      const eventName = `dashboard_${subtype.toLowerCase()}`.slice(0, 40);
      logEvent(a, eventName, sanitizeGa4Params(params));
    } catch (err) {
      console.error("[tracking] faTrack failed", err);
    }
  });
}
